import styled from "styled-components";

import Modal from "components/atoms/modal";
import { H2, H4, TextSm } from "components/atoms/text";
import Btn from "components/atoms/buttons";
import CheckBox from "components/atoms/input/CheckBox";
import InputPrice from "components/templates/project/SideBar/LiveProduct/InputPrice";
import InputRadio from "components/atoms/input/Radio";
import COLORS from "components/atoms/colors";

import useCategory from "apis/queries/useCategory";
import useLocation from "apis/queries/useLocation";
import usePurpose from "apis/queries/usePurpose";

import useInput from "hooks/useInput";
import filter from "datas/searchFilter";

import { ReactComponent as IconClose } from "assets/images/common/close.svg";

interface FilterState {
  origin: string;
  // resistPrinting?: string;
  locationId: number;
  purposeId: number;
  categoryIds: number[];
  prices: string[];
  priceLabel: string;
}

interface Props {
  initialFilter: FilterState;
  onClose: () => void;
  onChangeFilter: (obj: FilterState) => void;
}

// 검색 필터 모달
const ModalSearchFilter = ({
  initialFilter,
  onClose,
  onChangeFilter,
}: Props) => {
  const [filterState, dispatchFilterState] = useInput(initialFilter);

  // 사용 위치 필터 목록 api
  const locations = useLocation();
  const _locations = locations?.map((l) =>
    l.name === "전체" ? { ...l, id: 0 } : l
  );

  // 용도 필터 목록 api
  const purposes = usePurpose();
  const _purposes = purposes?.map((p) =>
    p.name === "전체" ? { ...p, id: 0 } : p
  );

  // 마감 필터 목록 api
  const categorys = useCategory();

  // 필터 적용하기 버튼 이벤트
  const handleApplyFilter = () => {
    onChangeFilter(filterState);
    onClose();
  };

  // 마감필터 체크박스 상태 변경 이벤트
  const handleChangeCategory = ({
    checked,
    categoryId,
  }: {
    checked: boolean;
    categoryId: number;
  }) => {
    if (checked) {
      const newCategoryIds = filterState.categoryIds.concat(categoryId);
      dispatchFilterState({
        option: { key: "categoryIds", value: newCategoryIds },
      });
    } else {
      const newCategoryIds = filterState.categoryIds.filter(
        (id) => categoryId !== id
      );
      dispatchFilterState({
        option: { key: "categoryIds", value: newCategoryIds },
      });
    }
  };

  return (
    <Modal.Container className="w-[810px] p-15 rounded-20 flex flex-col max-h-[80vh]">
      <button onClick={onClose} className="absolute right-10 top-10">
        <IconClose />
      </button>

      <H2 className="flex-none text-primary-active pb-10">검색필터</H2>

      <div className="flex-1 overflow-y-auto mr-[-20px] pr-[20px]">
        <div className="pl-2">
          <SubTitle>마감필터</SubTitle>
          <CheckList>
            {categorys?.map((category) => (
              <li key={category.id}>
                <CheckBox
                  name="categoryIds"
                  checked={filterState.categoryIds.includes(category.id)}
                  onChange={(e) =>
                    handleChangeCategory({
                      categoryId: category.id,
                      checked: e.target.checked,
                    })
                  }
                  children={<TextSm className="ml-1">{category.name}</TextSm>}
                />
              </li>
            ))}
          </CheckList>

          <SubTitle>원산지</SubTitle>
          <CheckList>
            {filter.origins.map((origin) => (
              <li key={origin.name}>
                <InputRadio
                  name="origin"
                  value={origin.value}
                  onChange={(event) => dispatchFilterState({ event })}
                  checked={filterState.origin === origin.value}
                  children={<TextSm>{origin.name}</TextSm>}
                />
              </li>
            ))}
          </CheckList>

          {/* 기획에서 제외됨 */}
          {/* <SubTitle>방염여부</SubTitle>
          <CheckList>
            {filter.resistPrintings.map((r) => (
              <li key={r.name}>
                <InputRadio
                  name="resistPrinting"
                  value={r.value}
                  onChange={(event) => dispatchFilterState({ event })}
                  checked={filterState.resistPrinting === r.value}
                  children={<TextSm>{r.name}</TextSm>}
                />
              </li>
            ))}
          </CheckList> */}

          <SubTitle>사용 위치</SubTitle>
          <CheckList>
            {_locations?.map((location) => (
              <li key={location.id}>
                <InputRadio
                  name="locationId"
                  value={location.id}
                  onChange={({ target }) =>
                    dispatchFilterState({
                      option: { key: target.name, value: Number(target.value) },
                    })
                  }
                  checked={filterState.locationId === location.id}
                  children={<TextSm>{location.name}</TextSm>}
                />
              </li>
            ))}
          </CheckList>

          <SubTitle>용도</SubTitle>
          <CheckList>
            {_purposes?.map((purpose) => (
              <li key={purpose.id}>
                <InputRadio
                  name="purposeId"
                  value={purpose.id}
                  onChange={({ target }) =>
                    dispatchFilterState({
                      option: { key: target.name, value: Number(target.value) },
                    })
                  }
                  checked={filterState.purposeId === purpose.id}
                  children={<TextSm>{purpose.name}</TextSm>}
                />
              </li>
            ))}
          </CheckList>

          <MeasureTitle>
            <p>
              단가<span>m²</span>
            </p>
          </MeasureTitle>
          <div>
            <InputPrice
              prices={filterState.prices}
              priceLabel={filterState.priceLabel}
              onChagePrices={(value) =>
                dispatchFilterState({ option: { key: "prices", value } })
              }
              onChangeLabel={(value) =>
                dispatchFilterState({ option: { key: "priceLabel", value } })
              }
            />
          </div>
        </div>
      </div>

      <div className="flex-none flex justify-end mt-10">
        <Btn.Blue
          onClick={handleApplyFilter}
          size="medium"
          radiusFull
          width="270px"
        >
          적용하기
        </Btn.Blue>
      </div>
    </Modal.Container>
  );
};

const SubTitle = styled(H4)`
  display: flex;
  gap: 20px;
  align-items: center;

  &::after {
    content: "";
    display: block;
    flex: 1;
    height: 1px;
    background: #d2d2d2;
  }
`;

const MeasureTitle = styled(SubTitle)`
  p {
    display: flex;
    gap: 4px;
    align-items: flex-end;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
  }

  span {
    font-size: 12px;
    color: ${COLORS.black["40%"]};
  }
`;

const CheckList = styled.ul`
  margin-top: 20px;
  margin-bottom: 40px;
  margin-left: 12px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  row-gap: 20px;
`;

export default ModalSearchFilter;
